import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Wind, Hand, Music, MessageCircle, Footprints, Volume2, Check, Clock, Gamepad2 } from "lucide-react";
import { http, formatApiError } from "@/lib/api";
import { speakMixed } from "@/lib/buddy";
import { useProfile } from "@/context/ProfileContext";
import { Milestones } from "./Milestones";
import { BabyJournal } from "./BabyJournal";
import { BabySigns } from "./BabySigns";
import { PlayMode } from "./play/PlayMode";

const ICON = { calm: Wind, sensory: Hand, song: Music, words: MessageCircle, move: Footprints };
const TINT = { calm: "bg-[#E3EEF9] text-[#1F5C99]", sensory: "bg-[#FBEBDD] text-[#9A4F1C]", song: "bg-[#F3E6F2] text-[#7A3D74]", words: "bg-[#FFF4D6] text-[#8A6212]", move: "bg-[#E1F1E7] text-[#2E7D4F]" };

const Activity = ({ a, cat, babyId, onToggle, voice }) => {
  const Icon = ICON[a.cat];
  return (
    <article className={`rounded-3xl border p-5 flex flex-col rise ${a.done_today ? "border-[#9CC5A9] bg-[#F4FAF5]" : "border-[#E6DEC3] bg-white"}`} data-testid={`baby-activity-${a.id}`}>
      <div className="flex items-center gap-2">
        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-medium ${TINT[a.cat]}`}><Icon className="w-3.5 h-3.5" />{cat}</span>
        <span className="ml-auto inline-flex items-center gap-1 text-xs text-stone-500"><Clock className="w-3 h-3" />{a.minutes} min</span>
      </div>
      <h3 className="font-display text-xl text-[#2C4A3E] mt-3">{a.title}</h3>
      <ol className="mt-2 space-y-1 text-sm text-stone-700 list-decimal pl-5 flex-1">{a.steps.map((s, i) => <li key={i}>{s}</li>)}</ol>
      <p className="mt-3 text-xs text-stone-500 italic">{a.why}</p>
      <div className="mt-4 flex flex-wrap gap-2">
        <button onClick={() => speakMixed(a.read, voice).catch(() => {})} data-testid={`baby-read-${a.id}`} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-[#F1EADB] text-sm text-[#2C4A3E] hover:bg-[#E9DFC9] transition-colors"><Volume2 className="w-4 h-4" />Read aloud to baby</button>
        <button onClick={() => onToggle(a)} data-testid={`baby-done-${a.id}`} aria-pressed={a.done_today}
          className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm transition-colors ${a.done_today ? "bg-[#2E7D4F] text-white" : "border border-[#C8DCCB] text-[#2C4A3E] hover:bg-[#EEF3EF]"}`}><Check className="w-4 h-4" />{a.done_today ? "Done today" : "We did it"}</button>
      </div>
    </article>
  );
};

export const BabyPlan = ({ baby }) => {
  const { buddy } = useProfile();
  const [track, setTrack] = useState(baby.track_id);
  const [p, setP] = useState(null);
  const [play, setPlay] = useState(false);
  const load = () => http.get(`/babies/${baby.id}/plan`, { params: { track } }).then((r) => setP(r.data)).catch((e) => toast.error(formatApiError(e)));
  useEffect(() => { load(); }, [track]); // eslint-disable-line react-hooks/exhaustive-deps
  const toggle = async (a) => {
    try {
      a.done_today ? await http.delete(`/babies/${baby.id}/activities/${a.id}/done`) : await http.post(`/babies/${baby.id}/activities/${a.id}/done`);
      if (!a.done_today) toast.success("Lovely moment together");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  if (!p) return <p className="mt-8 text-stone-500">Loading activities…</p>;
  const done = p.activities.filter((a) => a.done_today).length;
  return (
    <div className="mt-8 space-y-8" data-testid="baby-plan">
      {play && <PlayMode onClose={() => setPlay(false)} />}
      <div className="flex flex-wrap items-end gap-4">
        <div className="flex-1 min-w-[220px]">
          <p className="text-xs uppercase tracking-widest text-stone-500">{p.track.label}{track === baby.track_id ? ` · ${baby.name}'s stage` : ""}</p>
          <h3 className="font-display text-3xl text-[#2C4A3E]" data-testid="baby-track-theme">{p.track.theme}</h3>
          <p className="text-sm text-stone-600 mt-1" data-testid="baby-progress">{done} of {p.activities.length} done today · {p.week_count} {p.week_count === 1 ? "moment" : "moments"} this week</p>
        </div>
        <button onClick={() => setPlay(true)} data-testid="baby-play-btn" className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#F28C38] text-[#3B1D05] font-medium hover:scale-[1.03] transition-transform"><Gamepad2 className="w-5 h-5" />Play with {baby.name}</button>
        <label className="text-xs text-stone-600">Browse stages
          <select value={track} onChange={(e) => setTrack(e.target.value)} data-testid="baby-track-select" className="block mt-1 px-4 py-2.5 rounded-xl bg-white border border-[#D6CDB4] text-sm">
            {p.tracks.map((t) => <option key={t.id} value={t.id}>{t.label}{t.id === baby.track_id ? " (now)" : ""}</option>)}
          </select></label>
      </div>
      <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-5" data-testid="baby-activities">
        {p.activities.map((a) => <Activity key={a.id} a={a} cat={p.categories[a.cat]} babyId={baby.id} onToggle={toggle} voice={buddy?.voice} />)}
      </div>
      <BabySigns voice={buddy?.voice} />
      <div className="grid lg:grid-cols-2 gap-6">
        <Milestones babyId={baby.id} name={baby.name} items={p.milestones} label={p.track.label} onChange={load} />
        <BabyJournal babyId={baby.id} name={baby.name} />
      </div>
    </div>
  );
};
